const CHAT_SERVER_URL =
  process.env.REACT_APP_CHAT_SERVER_URL || "http://localhost:4000";

// Server "chatbot-apikey" expose endpoint Anthropic-compatible:
//   POST /anthropic/v1/messages  (SSE: message_start, content_block_delta, message_stop)
// Mặc định chạy ở cổng 8081. Có thể override qua REACT_APP_API_KEY_SERVER_URL.
const API_KEY_SERVER_URL =
  process.env.REACT_APP_API_KEY_SERVER_URL || "http://localhost:8081";

// Key dùng để lưu API key trong localStorage.
export const API_KEY_STORAGE_KEY = "pimath_api_key";

// API key fallback từ env (nếu có). localStorage sẽ được ưu tiên hơn.
const ENV_API_KEY = process.env.REACT_APP_API_KEY || "";

/**
 * Lấy API key hiện tại: ưu tiên localStorage, fallback về env.
 * Đọc tại thời điểm gọi để người dùng đổi key không cần reload.
 */
export function getApiKey() {
  try {
    const stored = localStorage.getItem(API_KEY_STORAGE_KEY);
    if (stored && stored.trim()) return stored.trim();
  } catch {
    // localStorage không khả dụng (SSR / private mode) → dùng env.
  }
  return ENV_API_KEY;
}

/** Lưu API key vào localStorage (xoá nếu rỗng). */
export function setApiKey(key) {
  try {
    const trimmed = String(key || "").trim();
    if (trimmed) {
      localStorage.setItem(API_KEY_STORAGE_KEY, trimmed);
    } else {
      localStorage.removeItem(API_KEY_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Không thể lưu API key:", error);
  }
}

/** Có API key hay chưa (dùng cho UI nhắc nhập). */
export function hasApiKey() {
  return !!getApiKey();
}

// Số token tối đa cho 1 lần trả lời (Anthropic yêu cầu max_tokens bắt buộc).
const DEFAULT_MAX_TOKENS = Number(process.env.REACT_APP_MAX_TOKENS) || 8192;

/**
 * Export AI response content to a downloadable file.
 *
 * @param {string} content - Text content to export
 * @param {string} format - File format (xlsx, docx, pptx, txt, md, js, py, go, etc.)
 * @param {string} filename - Desired filename (without extension)
 * @returns {Promise<void>} - Triggers browser download
 */
export async function exportToFile(content, format, filename = "export") {
  const res = await fetch(`${CHAT_SERVER_URL}/api/chat/export`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, format, filename }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Lỗi export: ${res.status}`);
  }

  // Lấy filename từ Content-Disposition header nếu có
  const disposition = res.headers.get("Content-Disposition") || "";
  let downloadName = `${filename}.${format}`;
  const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/i);
  if (match) {
    downloadName = decodeURIComponent(match[1]);
  }

  // Tạo blob và trigger download
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = downloadName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Send a file to the backend extract endpoint.
 * Returns { kind: "image", base64Url } or { kind: "doc", text }.
 */
export async function extractFileContent(file) {
  const fd = new FormData();
  fd.append("file", file);

  const res = await fetch(`${CHAT_SERVER_URL}/api/chat/extract`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Lỗi server ${res.status}`);
  }

  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────
//  Helpers: chuyển message (OpenAI-style) → Anthropic Messages API format
// ─────────────────────────────────────────────────────────────────────────

const DATA_URI_RE = /^data:([^;,]+)?(;base64)?,(.*)$/is;

/** Gộp content (string | array parts) thành plain text (dùng cho system). */
function contentToText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === "string") return part;
        if (part && typeof part === "object" && typeof part.text === "string")
          return part.text;
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

/** Chuyển 1 image_url (data URI hoặc http) → Anthropic image block. */
function toAnthropicImageBlock(url) {
  if (typeof url !== "string" || !url) return null;
  const m = url.match(DATA_URI_RE);
  if (m && m[2]) {
    // data:<media_type>;base64,<data>
    const mediaType = (m[1] || "image/png").toLowerCase();
    const data = m[3] || "";
    if (!data) return null;
    return {
      type: "image",
      source: { type: "base64", media_type: mediaType, data },
    };
  }
  if (/^https?:\/\//i.test(url)) {
    return { type: "image", source: { type: "url", url } };
  }
  return null;
}

/** Chuyển content (OpenAI-style) → Anthropic content (string | block[]). */
function toAnthropicContent(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return contentToText(content);

  const blocks = [];
  for (const part of content) {
    if (!part) continue;
    if (typeof part === "string") {
      if (part) blocks.push({ type: "text", text: part });
      continue;
    }
    if (part.type === "text" && typeof part.text === "string") {
      blocks.push({ type: "text", text: part.text });
    } else if (part.type === "image_url") {
      const rawUrl =
        typeof part.image_url === "string"
          ? part.image_url
          : part.image_url?.url;
      const img = toAnthropicImageBlock(rawUrl);
      if (img) blocks.push(img);
    } else if (part.type === "image" && part.source) {
      blocks.push(part);
    } else if (part.type === "file") {
      // File tài liệu: ưu tiên base64 (gửi thẳng bytes), fallback URL.
      if (typeof part.data === "string" && part.data) {
        blocks.push({
          type: "document",
          source: {
            type: "base64",
            media_type: part.mimeType || "application/octet-stream",
            data: part.data,
          },
          name: part.name || undefined,
        });
      } else {
        const url = part.file_url || part.url;
        if (typeof url === "string" && /^https?:\/\//i.test(url)) {
          blocks.push({
            type: "document",
            source: { type: "url", url },
            name: part.name || undefined,
            mimeType: part.mimeType || undefined,
          });
        }
      }
    } else if (part.type === "document" && part.source) {
      blocks.push(part);
    } else if (typeof part.text === "string" && part.text) {
      blocks.push({ type: "text", text: part.text });
    }
  }
  // Anthropic không cho content rỗng → fallback text rỗng an toàn.
  return blocks.length > 0 ? blocks : "";
}

/**
 * Chuẩn hoá chế độ thinking về 1 trong các giá trị:
 * "thinking" | "auto" | "fast" | "research".
 * "fast" = tắt suy luận. "research" = nghiên cứu chuyên sâu (suy luận + web search).
 * Mặc định "auto".
 */
export function normalizeThinkingMode(mode) {
  const v = String(mode || "").toLowerCase();
  if (v === "thinking" || v === "think" || v === "on") return "thinking";
  if (v === "fast" || v === "off" || v === "none" || v === "false")
    return "fast";
  if (v === "research" || v === "deep" || v === "deep_research")
    return "research";
  return "auto";
}

/**
 * Build body cho POST /anthropic/v1/messages.
 * - role "system" được gom vào field `system` (top-level).
 * - role khác giữ user/assistant, content chuyển sang Anthropic block.
 * - thinkingMode: "thinking" | "auto" | "fast" → server map sang Qwen featureConfig.
 */
function buildAnthropicBody({ model, messages, maxTokens, thinkingMode }) {
  let system = "";
  const anthropicMessages = [];

  for (const m of messages || []) {
    if (!m) continue;
    if (m.role === "system") {
      const text = contentToText(m.content);
      if (text) system += (system ? "\n\n" : "") + text;
      continue;
    }
    const role = m.role === "assistant" ? "assistant" : "user";
    anthropicMessages.push({ role, content: toAnthropicContent(m.content) });
  }

  if (anthropicMessages.length === 0) {
    anthropicMessages.push({ role: "user", content: "Hello" });
  }

  const mode = normalizeThinkingMode(thinkingMode);
  // "research" = nghiên cứu chuyên sâu: bật suy luận (thinking) + web search.
  const isResearch = mode === "research";
  const qwenThinkingMode = isResearch ? "thinking" : mode;

  const body = {
    model: model || "claude-3-7-sonnet",
    max_tokens: maxTokens || DEFAULT_MAX_TOKENS,
    stream: true,
    messages: anthropicMessages,
    // Custom field — server (chatbot-apikey) đọc để set Qwen thinking featureConfig.
    thinking_mode: qwenThinkingMode,
  };
  // Nghiên cứu chuyên sâu → server bật research_mode="deep" + auto_search.
  if (isResearch) {
    body.research_mode = "deep";
    body.deep_research = true;
    body.auto_search = true;
  }
  if (system) body.system = system;
  // Gửi kèm field thinking chuẩn Anthropic để tương thích chung.
  if (qwenThinkingMode === "fast") {
    body.thinking = { type: "disabled" };
  } else {
    body.thinking = { type: "enabled" };
  }
  return body;
}

/**
 * Xử lý 1 payload JSON (gộp từ các dòng `data:` của 1 SSE event Anthropic).
 * Các event quan tâm:
 *   - content_block_delta → text_delta (nội dung) / thinking_delta (suy nghĩ)
 *   - message_stop        → kết thúc
 *   - error               → lỗi stream
 */
function handleAnthropicPayload(payload, handlers) {
  const trimmed = String(payload || "").trim();
  if (!trimmed || trimmed === "[DONE]") return;

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return;
  }

  const type = parsed?.type;

  if (type === "error" || parsed?.error) {
    const err = parsed?.error;
    const message =
      (err && typeof err === "object" && err.message) ||
      (typeof err === "string" ? err : "") ||
      "Stream error";
    handlers.error(new Error(message));
    return;
  }

  if (type === "content_block_delta") {
    const delta = parsed.delta || {};
    if (delta.type === "text_delta" && typeof delta.text === "string") {
      handlers.content(delta.text);
    } else if (
      delta.type === "thinking_delta" &&
      typeof delta.thinking === "string"
    ) {
      handlers.thinking(delta.thinking);
    }
    return;
  }

  if (type === "message_stop") {
    handlers.finish();
    return;
  }

  // message_start / ping / content_block_start / content_block_stop /
  // message_delta → không cần xử lý cho UI chat.
}

/**
 * Stream a chat completion từ server "chatbot-apikey" qua endpoint
 * Anthropic-compatible: POST /anthropic/v1/messages.
 *
 * Server trả về Anthropic SSE (message_start → content_block_delta(text_delta)
 * → message_stop). Dùng XMLHttpRequest/onprogress để cập nhật real-time, tránh
 * buffer của fetch reader.
 *
 * Lưu ý: endpoint này stateless (không tự lưu hội thoại) nên onDone trả về
 * savedId = null. Việc lưu lịch sử cần xử lý phía caller nếu cần.
 *
 * @returns {function} abort
 */
export function streamChat({
  model,
  messages,
  conversationId, // eslint-disable-line no-unused-vars
  title, // eslint-disable-line no-unused-vars
  projectId, // eslint-disable-line no-unused-vars
  maxTokens,
  thinkingMode,
  onChunk,
  onThinkingChunk,
  onDone,
  onError,
}) {
  const xhr = new XMLHttpRequest();

  let lastIndex = 0;
  let buffer = "";
  let doneCalled = false;
  let aborted = false;

  const finish = () => {
    if (doneCalled || aborted) return;
    doneCalled = true;
    // Endpoint Anthropic stateless → không có savedId từ server.
    onDone?.(null);
  };

  const fail = (err) => {
    if (doneCalled || aborted) return;
    doneCalled = true;
    onError?.(err);
  };

  const handlers = {
    thinking: (delta) => {
      if (!aborted && delta) onThinkingChunk?.(delta);
    },
    content: (delta) => {
      if (!aborted && delta) onChunk?.(delta);
    },
    finish,
    error: fail,
  };

  /**
   * Tách 1 khối SSE event (các dòng ngăn nhau bởi \n), gom mọi dòng `data:`
   * lại rồi parse 1 lần. Bỏ qua dòng `event:` / comment `:`.
   */
  const processEventBlock = (block) => {
    if (aborted || !block) return;
    const dataLines = [];
    for (const rawLine of block.split(/\r?\n/)) {
      const line = rawLine.trimEnd();
      if (!line || line.startsWith(":") || line.startsWith("event:")) continue;
      if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    }
    if (dataLines.length === 0) return;
    handleAnthropicPayload(dataLines.join("\n"), handlers);
  };

  const processText = (text) => {
    if (aborted || !text) return;
    buffer += text;
    // SSE events ngăn cách bởi dòng trống (\n\n). Giữ lại phần cuối chưa đủ.
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";
    for (const block of blocks) {
      if (aborted) return;
      processEventBlock(block);
    }
  };

  const url = `${API_KEY_SERVER_URL}/anthropic/v1/messages`;
  xhr.open("POST", url, true);

  // CORS server chỉ allow header: Content-Type, Authorization.
  xhr.setRequestHeader("Content-Type", "application/json");
  xhr.setRequestHeader("Accept", "text/event-stream");
  const apiKey = getApiKey();
  if (apiKey) {
    xhr.setRequestHeader("Authorization", `Bearer ${apiKey}`);
  }

  xhr.onprogress = () => {
    if (aborted) return;
    const currentText = xhr.responseText || "";
    const newText = currentText.slice(lastIndex);
    lastIndex = currentText.length;
    processText(newText);
  };

  xhr.onload = () => {
    if (aborted) return;

    // HTTP lỗi: cố parse JSON error của server (Anthropic shape).
    if (xhr.status < 200 || xhr.status >= 300) {
      let message = `Server lỗi ${xhr.status}`;
      try {
        const parsed = JSON.parse(xhr.responseText || "{}");
        message = parsed?.error?.message || parsed?.message || message;
      } catch {
        if (xhr.responseText) message = xhr.responseText;
      }
      fail(new Error(message));
      return;
    }

    // Flush nốt phần buffer còn lại.
    if (buffer.trim()) {
      processEventBlock(buffer);
      buffer = "";
    }

    finish();
  };

  xhr.onerror = () => {
    if (aborted) return;
    fail(new Error("Không thể kết nối tới server stream."));
  };

  xhr.onabort = () => {
    aborted = true;
  };

  xhr.ontimeout = () => {
    if (aborted) return;
    fail(new Error("Stream timeout."));
  };

  xhr.timeout = 60 * 60 * 1000;

  xhr.send(
    JSON.stringify(
      buildAnthropicBody({ model, messages, maxTokens, thinkingMode }),
    ),
  );

  return () => {
    if (aborted || doneCalled) return;

    aborted = true;

    xhr.onprogress = null;
    xhr.onload = null;
    xhr.onerror = null;
    xhr.onabort = null;
    xhr.ontimeout = null;

    try {
      xhr.abort();
    } catch {
      // ignore
    }
  };
}
